import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import PageBase from 'components/PageBase';
import {
  Button,
  Typography,
  Chip
} from '@material-ui/core';
import useRouter from 'utils/useRouter';
import {
  ROLE_CLIENT,
  PERPAGE
} from 'config';
import * as API from 'services/api';
import { setLoading } from 'redux/actions/app';
import useHomeContext from 'layouts/HomeLayout/context'
import Tabs from './tab';
import _ from 'lodash';
import useStyles from '../styles';
import { columnCreatedAt, columnId, filterTableData, exportClientsHeader, columnUser, columnActions } from 'pages/common';
import ActionGroup from 'components/ActionGroup';
import ExportCSV from 'components/ExportCSV';
import DataTable from 'components/PageBase/dataTable';
import { showMessage } from 'utils';

const PERPAGE_EXPORT = 10000;
const Clients = ({ intl: { formatMessage } }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { hasPermission, setImportUsersModalVisible, onDeleteById } = useHomeContext();
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [allUsersLoaded, setAllUsersLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [exportType, setExportType] = useState();

  const router = useRouter();

  const [openFilter, setOpenFilter] = useState(false)
  const [filters, setFilters] = useState({})
  const [keyword, setKeyword] = useState('')

  useEffect(() => {
    loadUsers(true);
  }, [page]);
  useEffect(() => {
    if (page == 0) {
      loadUsers(true);
    }
    else setPage(0);
  }, [keyword]);

  const loadUsers = (refresh) => {

    setUsersLoading(true);
    Promise.all([
      API.listUsers({
        page: page + 1,
        perPage: PERPAGE,
        role: ROLE_CLIENT,
        ...keyword && keyword.length > 0 ? { keyword } : {}
      }),
    ])
      .then(([res1]) => {

        let newUsers = page == 0 ? [] : users;
        if (res1.code === 200) {
          newUsers = [...newUsers, ...res1.users];
        }

        setUsers(newUsers)
        if (res1.users.length < PERPAGE) {
          setAllUsersLoaded(true)
        }
        dispatch(setLoading(false));
        setUsersLoading(false);
      }).catch(err => {
        dispatch(setLoading(false));
        setUsersLoading(false);
      });
  };

  const loadMore = _.debounce(() => {
    if (allUsersLoaded || usersLoading) {
      return;
    }
    setPage(prev => prev + 1);
  }, 500);

  const loadExportData = (page, exportMode) => {
    dispatch(setLoading(true));
    API.listUsers({ page, perPage: PERPAGE_EXPORT, role: ROLE_CLIENT, ...keyword && keyword.length > 0 ? { keyword } : {} }).then(res => {
      if (res.code === 200) {
        let newUsers = res.users;
        setData(prev => [...prev, ...newUsers]);
        if (res.users.length < PERPAGE_EXPORT) {
          dispatch(setLoading(false));
          setTimeout(() => {
            setExportType(exportMode);
          }, 500);
        } else {
          loadExportData(page + 1, exportMode);
        }
      }
    });
  };

  const onSendNotofication = (id) => {
    dispatch(setLoading(true));
    API.sendNotification({ id }).then(res => {
      dispatch(setLoading(false));
      if (res.code === 200) {
        showMessage({
          message: 'sent successfully',
          type: 'success',
        });
      }
    }).catch(err => {
      dispatch(setLoading(false));
    });
  }

  const onAddClient = () => {
    router.history.push('/clients/create');
  };


  const tableColumns = [
    columnId(formatMessage),
    {
      name: 'grantComptes',
      label: "Type",
      options: {
        filter: true,
        sort: true,
        customBodyRender: useCallback(value => <Chip color={value ? 'primary' : 'secondary'} variant='outlined' label={value ? "Grands Comptes" : "Client"} size="small" />, [])
      },
    },
    {
      name: 'company',
      label: formatMessage({
        id: 'company',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'siret',
      label: formatMessage({
        id: 'siret',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    ...columnUser(formatMessage),
    columnCreatedAt(formatMessage),
    columnActions(
      useCallback((value, tableMeta, updateValue) => {
        const record = users.find(item => item.id == tableMeta.rowData[0])
        return (
          <ActionGroup
            onDelete={() => {
              onDeleteById(record.id, "deleteUser"); // id
            }}
            onEdit={() => {
              router.history.push(`/client`, { id: tableMeta.rowData[0], data: record });
            }}
            onSend={() => onSendNotofication(record.id)}
          >
          </ActionGroup>
        )
      }, [users])
    )
  ];

  const datasource = filterTableData(users, filters);
  const toggleUsersImportModal = () => {
    setImportUsersModalVisible({
      addFree: true,
      apiName: "importUsers",
      title: formatMessage({
        id: 'home.client.import',
      }),
      modelUrl: "https://console-espaceclient.apave.com/doc/client.xlsx",
      hideClient: true
    });
  };

  if (!hasPermission("clients")) return <Typography variant='h4' style={{ textAlign: 'center', marginTop: "30vh" }}><strong>Permission Denied!</strong></Typography>
  return (
    <PageBase title={formatMessage({ id: 'navbar.entreprisesfrance' })}
      rightComponent={<div className={classes.rightdom}>
        <Button
          onClick={toggleUsersImportModal}
          classes={{ root: classes.excel }}
        >
          <FormattedMessage id="home.client.import" />
        </Button>
        <Button onClick={onAddClient} classes={{ root: classes.excel }}>
          <FormattedMessage id="home.client.add_client" />
        </Button>
        <ExportCSV
          filename="clients"
          headers={exportClientsHeader(formatMessage)}
          data={data}
          loadExportData={loadExportData}
          exportType={exportType}
          setExportType={setExportType}
        />
      </div>}
    >
      <div>
        <Tabs value={"france"} />
        <DataTable
          dataLoading={usersLoading}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          setFilters={setFilters}
          keyword={keyword}
          setKeyword={setKeyword}
          loadMore={loadMore}
          allUsersLoaded={allUsersLoaded}
          tableColumns={tableColumns}
          datasource={datasource}
          data={users}
        />
        <br />
        <br />
        <br />
      </div>
    </PageBase>
  );
};

Clients.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
};

export default injectIntl(Clients);
